<template>
  <div class="orangehrm-paper-container">
    <div class="orangehrm-card-container">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{
          scope === 'team'
            ? 'Team Remote Work Requests'
            : 'My Remote Work Requests'
        }}
      </oxd-text>
      <oxd-text v-if="scope === 'team'" class="remote-work-help">
        Review the schedule, worksite, and signed agreement before taking
        action. Supervisor approval is followed by any required management
        reviews and final Executive Director approval.
      </oxd-text>
      <oxd-form :loading="isLoading" @submit-valid="onSearch" @reset="onReset">
        <oxd-form-row>
          <oxd-grid :cols="3" class="orangehrm-full-width-grid">
            <oxd-grid-item>
              <oxd-input-field
                v-model="status"
                type="select"
                label="Status"
                :options="statusOptions"
              />
            </oxd-grid-item>
          </oxd-grid>
        </oxd-form-row>
        <oxd-form-actions>
          <oxd-button type="reset" label="Reset" display-type="ghost" />
          <oxd-button type="submit" label="Search" display-type="secondary" />
        </oxd-form-actions>
      </oxd-form>
      <oxd-text v-if="error" class="remote-work-error" role="alert">
        {{ error }}
      </oxd-text>
    </div>
    <table-header :selected="0" :total="total" :loading="isLoading" />
    <div class="orangehrm-container">
      <oxd-card-table
        :headers="headers"
        :items="rows"
        :selectable="false"
        :clickable="false"
        :loading="isLoading"
        row-decorator="oxd-table-decorator-card"
      />
    </div>
    <div class="orangehrm-bottom-container">
      <oxd-text
        v-if="!isLoading && !error && total === 0"
        class="remote-work-help"
      >
        No remote work requests match this view.
      </oxd-text>
      <oxd-pagination v-if="pages > 1" v-model:current="page" :length="pages" />
    </div>
    <remote-work-details
      v-if="selectedId"
      :request-id="selectedId"
      @close="selectedId = null"
      @changed="loadRequests"
      @revise="onRevise"
    />
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import {formatDate, parseDate} from '@/core/util/helper/datefns';
import useDateFormat from '@/core/util/composable/useDateFormat';
import RemoteWorkDetails from './RemoteWorkDetails.vue';
import {
  REMOTE_WORK_PATH,
  remoteWorkError,
  remoteWorkSchedule,
  remoteWorkStatuses,
} from '@/orangehrmLeavePlugin/util/remote-work';

export default {
  name: 'RemoteWorkRequests',
  components: {'remote-work-details': RemoteWorkDetails},
  props: {
    scope: {type: String, default: 'mine'},
  },
  emits: ['revise', 'metadata'],
  setup() {
    const http = new APIService(window.appGlobal.baseUrl, REMOTE_WORK_PATH);
    http.setIgnorePath(REMOTE_WORK_PATH);
    const {jsDateFormat} = useDateFormat();
    return {http, jsDateFormat};
  },
  data() {
    return {
      isLoading: false,
      error: '',
      rows: [],
      total: 0,
      page: 1,
      pageSize: 25,
      status: null,
      selectedId: null,
      statusOptions: Object.entries(remoteWorkStatuses).map(([id, label]) => ({
        id,
        label,
      })),
      headers: [
        {name: 'employeeName', title: 'Employee', style: {flex: 1}},
        {name: 'dates', title: 'Effective dates', style: {flex: 1.2}},
        {name: 'schedule', title: 'Schedule', style: {flex: 2}},
        {name: 'statusLabel', title: 'Status', style: {flex: 1.5}},
        {
          name: 'actions',
          title: 'Actions',
          slot: 'footer',
          cellType: 'oxd-table-cell-actions',
          style: {flex: 1},
          cellConfig: {
            view: {
              onClick: (row) => {
                this.selectedId = row.id;
              },
              component: 'oxd-button',
              props: {
                label: 'View details',
                displayType: 'text',
                size: 'medium',
              },
            },
          },
        },
      ],
    };
  },
  computed: {
    pages() {
      return Math.ceil(this.total / this.pageSize);
    },
  },
  watch: {
    page() {
      this.loadRequests();
    },
  },
  beforeMount() {
    this.loadRequests();
  },
  methods: {
    async loadRequests() {
      this.isLoading = true;
      this.error = '';
      try {
        const {data} = await this.http.getAll({
          scope: this.scope,
          status: this.status?.id || undefined,
          limit: this.pageSize,
          offset: (this.page - 1) * this.pageSize,
        });
        this.total = data.meta.total;
        this.rows = data.data.map((request) => ({
          id: request.id,
          employeeName: [
            request.employee?.firstName,
            request.employee?.lastName,
          ]
            .filter(Boolean)
            .join(' '),
          dates: `${this.displayDate(request.fromDate)} – ${this.displayDate(
            request.toDate,
          )}`,
          schedule: remoteWorkSchedule(request),
          statusLabel:
            remoteWorkStatuses[request.status] || request.statusLabel,
        }));
        this.$emit('metadata', data.meta);
        if (this.page > Math.max(1, this.pages)) {
          this.page = Math.max(1, this.pages);
        }
      } catch (error) {
        this.rows = [];
        this.total = 0;
        this.error = remoteWorkError(
          error,
          'Requests could not be loaded. Please try searching again.',
        );
      } finally {
        this.isLoading = false;
      }
    },
    displayDate(date) {
      return formatDate(parseDate(date), this.jsDateFormat) || date;
    },
    onSearch() {
      if (this.page !== 1) this.page = 1;
      else this.loadRequests();
    },
    onReset() {
      this.status = null;
      this.onSearch();
    },
    onRevise(request) {
      this.selectedId = null;
      this.$emit('revise', request);
    },
  },
};
</script>

<style lang="scss" scoped>
.remote-work-help {
  margin: 0.75rem 0;
}
.remote-work-error {
  margin-top: 1rem;
  padding: 1rem;
  border-left: 3px solid $oxd-feedback-danger-color;
}
</style>
